import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import Container from '../components/Container.jsx'
import PageHeader from '../components/PageHeader.jsx'
import Reveal from '../components/Reveal.jsx'
import CTA from '../components/CTA.jsx'

/* =========================================================================
   Projects — the full archive behind the homepage's "View all projects".
   The homepage teases the five most recent; everything Spectranox has
   shipped lives here, filterable by the same category tabs.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'
import { showcaseImage } from '../lib/showcaseImage.js'
import CountryTag from '../components/CountryTag.jsx'
import { fetchWithFallback } from '../lib/contentFallback.js'


// Same rotation the homepage spotlight uses, so a project keeps its accent
// colour across both pages.
const ACCENTS = ['#67e8f9', '#a78bfa', '#38bdf8', '#fb9a8f', '#fbbf24', '#34d399']

const resolveImage = showcaseImage

function ProjectCard({ project, index }) {
  const accent = ACCENTS[index % ACCENTS.length]
  return (
    <Reveal delay={(index % 3) * 80} className="h-full">
      <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:shadow-2xl">
        {/* 16:9 ground, image fitted inside it so no screenshot is cropped */}
        <div className="relative aspect-video overflow-hidden bg-ink-950">
          <img
            src={resolveImage(project.project_image)}
            alt={project.title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-contain"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
          />
        </div>

        <div className="flex flex-1 flex-col p-5">
          {/* Which market the work was built for, read before the name. */}
          <CountryTag code={project.country_code} className="mb-3 self-start" />

          <div className="flex items-center gap-2">
            <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
            <h2 className="font-display text-lg font-bold tracking-tight text-white">{project.title}</h2>
          </div>

          {project.description && (
            <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{project.description}</p>
          )}

          <Link
            to={`/portfolio/${project.id}`}
            className="group/cta mt-5 inline-flex items-center gap-2 self-start rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-[13px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-azure-400/60 hover:bg-azure-400/10"
          >
            View project
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      </article>
    </Reveal>
  )
}

export default function Projects() {
  const [projects, setProjects] = useState([])
  const [status, setStatus] = useState('loading') // loading | ready | error
  /* True when the content came from the build-time snapshot because the
     API could not be reached. The rows are shown either way. */
  const [offline, setOffline] = useState(false)

  useEffect(() => {
    let cancelled = false

    /* Falls back to the content baked at build time, so an outage shows the
       real portfolio rather than an empty page. */
    fetchWithFallback(
      `${API_BASE}/get-showcase-projects.php`,
      (d) => (d?.success && Array.isArray(d.projects) ? d.projects : null),
      'projects'
    ).then(({ data, usedFallback }) => {
      if (cancelled) return
      setProjects(data || [])
      setStatus('ready')
      setOffline(usedFallback)
    })

    return () => { cancelled = true }
  }, [])


  return (
    <>
      <Seo
        title="Portfolio"
        description="Websites, apps, social campaigns and training platforms Spectranox has designed, built and shipped for clients."
        path="/portfolio"
      />
      <main className="pt-20">
        <PageHeader
          title="Featured projects"
          subtitle="Client platforms across websites, apps, social media and training: the work behind the case studies."
        />

        <section className="py-16">
          <Container>
            {status === 'loading' && (
              <p className="py-16 text-center text-sm text-slate-500">Loading projects…</p>
            )}

            {status === 'error' && (
              <p className="py-16 text-center text-sm text-slate-500">
                We couldn't load the project list just now. Please refresh, or{' '}
                <Link to="/contact" className="text-brand-300 underline underline-offset-4">get in touch</Link>.
              </p>
            )}

            {status === 'ready' && projects.length === 0 && (
              <p className="py-16 text-center text-sm text-slate-500">
                No projects in this category yet, ask us about upcoming work.
              </p>
            )}

            {status === 'ready' && projects.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {projects.map((p, i) => (
                  <ProjectCard key={p.id} project={p} index={i} />
                ))}
              </div>
            )}
          </Container>
        </section>

        <CTA />
      </main>
    </>
  )
}
